import React, { Fragment, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  ProposalAdvisorConversationDeleteMessageConfirmation,
  ProposalAdvisorConversationDeleteMessageConfirmationState,
} from './ProposalAdvisorConversationDeleteMessageConfirmation'
import { ProposalAdvisorConversationMessage } from './ProposalAdvisorConversationMessage'
import {
  ProposalAdvisorConversationMessageForm,
  ProposalAdvisorConversationMessageFormHandle,
  ProposalAdvisorConversationMessageFormState,
} from './ProposalAdvisorConversationMessageForm'
import { ProposalAdvisorConversationMobileHeader } from './ProposalAdvisorConversationMobileHeader'
import { AdvisorConversation, AdvisorConversationMessage } from './types'
import { ProposalModalHeader } from '../../proposal-modal-header/ProposalModalHeader'
import useDateFormatter from '../../../../lib/hooks/useDateFormatter'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../../../../shadcn/components/ui/dialog'
import { DialogBackdropContext } from '../../../../shadcn/context/dialog/dialog-backdrop.context'
import { cn } from '../../../../shadcn/utils'

type ProposalAdvisorConversationChatProps = Readonly<
  AdvisorConversation & {
    proposal: Readonly<{
      title: string
      adults: number
      picture?: string
      startDate: Date
      endDate: Date
    }>
    quit: () => void
  }
>

function useChatDateFormatter() {
  const formatter = useDateFormatter()
  formatter.updateTemplate('date', {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  })
  return (date: Date) =>
    formatter.format({
      date,
      formatString: 'date',
    })
}

function areMessagesOnSameDate(
  a: AdvisorConversationMessage,
  b: AdvisorConversationMessage,
) {
  const { sentAt: aSentAt } = a
  const { sentAt: bSentAt } = b
  return (
    aSentAt.getDay() === bSentAt.getDay() &&
    aSentAt.getMonth() === bSentAt.getMonth() &&
    aSentAt.getFullYear() === bSentAt.getFullYear()
  )
}

export function ProposalAdvisorConversationChat({
  quit,
  add,
  messages,
  proposal,
}: ProposalAdvisorConversationChatProps) {
  const [formState, setFormState] =
    useState<ProposalAdvisorConversationMessageFormState>(
      add !== undefined ? 'ready' : 'disabled',
    )
  const [messageToBeDeletedIndex, setMessageToBeDeletedIndex] = useState<
    undefined | number
  >()
  const [deletionState, setDeletionState] =
    useState<ProposalAdvisorConversationDeleteMessageConfirmationState>('ready')

  const formHandle =
    useRef<ProposalAdvisorConversationMessageFormHandle | null>(null)

  const formatDate = useChatDateFormatter()

  const scrollToLastMessageRef = (node: HTMLDivElement | null) => {
    if (!node) {
      return
    }
    node.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSend = (content: string) => {
    setFormState('sending')
    add!(content)
      .then(() => formHandle.current!.reset())
      .finally(() => setFormState('ready'))
  }

  const handleDelete = () => {
    const message = messages[messageToBeDeletedIndex!]
    setDeletionState('deleting')
    message.delete!()
      .then(() => {
        setMessageToBeDeletedIndex(undefined)
      })
      .finally(() => {
        setDeletionState('ready')
      })
  }

  return (
    <>
      <Dialog defaultOpen onOpenChange={(isOpen) => !isOpen && quit?.()}>
        <DialogContent
          backdropStyle='dark'
          className='flex flex-col p-0 md:h-[554px]'
        >
          <DialogTitle className='sr-only'>
            <FormattedMessage
              id='ui.ProposalAdvisorConversationChat.title'
              defaultMessage='Conversation avec le conseiller'
            />
          </DialogTitle>
          <article className='flex h-full flex-col overflow-y-hidden'>
            <div className='hidden md:block'>
              <ProposalModalHeader
                startDate={proposal.startDate}
                endDate={proposal.endDate}
                title={proposal.title}
                adults={proposal.adults}
                picture={proposal.picture}
                onClose={quit}
              />
            </div>
            <div className='md:hidden'>
              <ProposalAdvisorConversationMobileHeader
                onClose={quit}
                title={proposal.title}
              />
            </div>
            <hr className='border-border' />
            <div className='flex flex-col gap-3 overflow-y-auto p-3'>
              {messages.map((message, i, all) => {
                const { author, sentAt, content, delete: deleteFn } = message
                return (
                  <Fragment key={i}>
                    {(i === 0 ||
                      !areMessagesOnSameDate(message, all[i - 1])) && (
                      <time
                        dateTime={formatDate(sentAt)}
                        className='self-center text-sm text-grey-700'
                      >
                        {formatDate(sentAt)}
                      </time>
                    )}
                    <ProposalAdvisorConversationMessage
                      ref={
                        i === all.length - 1
                          ? scrollToLastMessageRef
                          : undefined
                      }
                      flexOrientation={
                        author.isMe ? 'right-to-left' : 'left-to-right'
                      }
                      {...(deleteFn
                        ? { onDelete: () => setMessageToBeDeletedIndex(i) }
                        : {})}
                      message={{
                        color: author.isMe ? 'turquoise' : 'grey',
                        author: {
                          hideName: author.isMe,
                          firstname: author.firstname,
                          lastname: author.lastname,
                          ...(author.profilePicture
                            ? { profilePicture: author.profilePicture }
                            : {}),
                        },
                        sentAt,
                        ...(content ? { content } : {}),
                      }}
                      className={cn(
                        'max-w-[75%]',
                        author.isMe ? 'self-end' : 'self-start',
                      )}
                    />
                  </Fragment>
                )
              })}
            </div>
            <hr className='mt-auto border-border' />
            <ProposalAdvisorConversationMessageForm
              onSubmit={handleSend}
              state={formState}
              ref={formHandle}
            />
          </article>
        </DialogContent>
      </Dialog>
      {messageToBeDeletedIndex !== undefined && (
        <DialogBackdropContext.Provider value={{ visible: true }}>
          <ProposalAdvisorConversationDeleteMessageConfirmation
            confirm={() => handleDelete()}
            quit={() => setMessageToBeDeletedIndex(undefined)}
            state={deletionState}
          />
        </DialogBackdropContext.Provider>
      )}
    </>
  )
}

export default ProposalAdvisorConversationChat
